import { useEffect, useRef } from 'react';

interface Props {
  parts: string[];
  /** selected part (the lesson page lists parts in its side panel) */
  idx: number;
  onProgress: (position: number) => void;
  onComplete: () => void;
}

/**
 * Rutube embed with a part selector. Playback progress arrives through the
 * player's postMessage API when the embed supports it; otherwise time on
 * screen is the only signal, so completion is inferred conservatively.
 */
export function Player({ parts, idx, onProgress, onComplete }: Props) {
  const done = useRef(false);
  const lastBucket = useRef(0);
  const src = parts[idx];

  useEffect(() => {
    done.current = false;
    lastBucket.current = 0;
    const onMsg = (e: MessageEvent) => {
      if (typeof e.origin !== 'string' || !/(^|\.)rutube\.ru$/.test(new URL(e.origin).hostname)) return;
      let data: { type?: string; data?: { time?: number; duration?: number } } | null = null;
      try {
        data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (!data || data.type !== 'player:currentTime' || !data.data?.duration) return;
      const pos = Math.min(1, (data.data.time ?? 0) / data.data.duration);
      const bucket = Math.floor(pos * 4) / 4;
      if (bucket > lastBucket.current) {
        lastBucket.current = bucket;
        onProgress(bucket);
      }
      if (pos >= 0.9 && !done.current) {
        done.current = true;
        onComplete();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [idx, onProgress, onComplete]);

  if (!src) {
    return (
      <div className="player">
        <div className="player__empty">Видео ещё не добавлено. Загляните позже.</div>
      </div>
    );
  }
  return (
    <div className="player">
      <iframe key={src} src={src} title={`Видео, часть ${idx + 1}`} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen loading="eager" />
    </div>
  );
}
